import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { detectIncognito } from 'detectincognitojs';

interface IncognitoGuardProps {
  children: React.ReactNode;
}

/**
 * Quotes shown when user is NOT in incognito/private mode
 */
const QUOTES: { text: string; author?: string }[] = [
  // Modern privacy context
  {
    text: "Privacy is not an option, and it shouldn't be the price we accept for just getting on the Internet.",
    author: 'Glen Greenwald',
  },
  {
    text: "Arguing that you don't care about the right to privacy because you have nothing to hide is no different than saying you don't care about free speech because you have nothing to say.",
    author: 'Edward Snowden',
  },
  {
    text: 'When we give up a little privacy for a little convenience, eventually we surrender both.',
    author: 'Unknown',
  },
  {
    text: 'A lack of privacy can make the present unbearable and the future insecure.',
    author: 'Unknown',
  },

  // Bhagavad Gita & Hindu philosophy
  { text: 'You have the right to perform your duty, but not to the fruits of your actions.', author: 'Bhagavad Gita 2.47' },
  { text: 'The soul is neither born, nor does it ever die.', author: 'Bhagavad Gita 2.20' },
  { text: 'For one who has conquered the mind, the mind is the best of friends.', author: 'Bhagavad Gita 6.6' },
  { text: 'The mind is restless, turbulent, strong, and obstinate.', author: 'Bhagavad Gita 6.34' },
  { text: 'A person is said to be elevated when he is not disturbed by happiness and distress.', author: 'Bhagavad Gita 2.15' },
  { text: 'Truth is one; the wise call it by many names.', author: 'Rig Veda 1.164.46' },

  // General Hindu wisdom
  { text: 'Inner silence is the source of true strength.', author: 'Hindu Philosophy' },
  { text: 'Peace comes from within, not from without.', author: 'Hindu Wisdom' },
];

const GRADIENT_BACKGROUNDS = [
  // Saffron → Ivory (Indian spiritual tone)
  'linear-gradient(135deg, #FFF8E1, #FFE0B2)',

  // Lotus pink → Soft lavender
  'linear-gradient(135deg, #FCE4EC, #F3E5F5)',

  // Dawn sky → Calm blue
  'linear-gradient(135deg, #E3F2FD, #E1F5FE)',

  // Forest calm → Mist
  'linear-gradient(135deg, #E8F5E9, #F1F8E9)',

  // Manuscript parchment
  'linear-gradient(135deg, #FFFDE7, #FAF3E0)',
];

const IncognitoGuard: React.FC<IncognitoGuardProps> = ({ children }) => {
  const [isPrivate, setIsPrivate] = useState<boolean | null>(null);

  /**
   * Pick quote + background ONCE per component mount
   */
  const [selection] = useState(() => {
    const idx = Math.floor(Math.random() * QUOTES.length);
    return {
      quote: QUOTES[idx],
      // modulo ensures we always have a background
      bg: GRADIENT_BACKGROUNDS[idx % GRADIENT_BACKGROUNDS.length],
    };
  });

  useEffect(() => {
    detectIncognito()
      .then((result) => {
        console.log('Incognito detection:', result);
        setIsPrivate(result.isPrivate);
      })
      .catch((err) => {
        console.error('Incognito detection error:', err);
        // On error, allow access
        setIsPrivate(true);
      });
  }, []);

  // Still checking
  if (isPrivate === null) {
    return null;
  }

  // NOT in incognito/private mode
  if (!isPrivate) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: selection.bg,
        }}
      >
        <Box
          sx={{
            maxWidth: 800,
            textAlign: 'center',
            px: 4,
            py: 5,
            backgroundColor: 'rgba(255,255,255,0.85)',
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            animation: 'fadeIn 0.8s ease',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(12px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography
            sx={{
              fontSize: 22,
              color: '#000',
              fontStyle: 'italic',
              mb: 1,
            }}
          >
            “{selection.quote.text}”
          </Typography>

          {selection.quote.author && (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              — {selection.quote.author}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // In incognito/private mode → render app
  return <>{children}</>;
};

export default IncognitoGuard;
